import {
  ExpandLess,
  ExpandMore,
  Description as FileIcon,
  Sell,
  Source,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname, parseError } from "../utils";
import IconMenuItem from "./IconMenuItem";

interface DocumentScopeSelectorProps {
  value: string;
  onChange: (value: string, documentPath?: string) => void;
  companyId: string;
  companyName: string;
  getToken: () => Promise<string>;
  eventListener: Function;
  sx?: any;
  tooltip?: React.ReactNode;
  disabled?: boolean;
}

// Helper function to get folder display properties
const getFolderDisplayProps = (
  folder: any,
  companyName: string
): {
  displayName: string;
  iconColor: "warning" | "primary" | "inherit";
  textColor?: string;
} => {
  const isSharedFolder = folder.name === "shared" && folder.path === "/";
  const isExternalRoot = folder.isExternalRoot === true;
  const isExternal = folder.isExternal === true;
  const isPublicRoot = folder.isPublicRoot === true;
  const isPublic = folder.isPublic === true;
  const isCompanyShared = folder.isCompanyShared === true;

  // Determine icon color based on shareVisibility
  let iconColor: "warning" | "primary" | "inherit" = "inherit";
  if (folder.shareVisibility === "public") {
    iconColor = "warning";
  } else if (
    folder.shareVisibility === "direct" ||
    folder.shareVisibility === "company"
  ) {
    iconColor = "primary";
  }

  // Child folders under external root, or public folders
  if (isExternal || isPublicRoot || isPublic) {
    const displayName =
      (isPublic || isExternal) && folder.ownerCompany
        ? `${folder.name} (${folder.ownerCompany.name})`
        : folder.name;
    return {
      displayName,
      iconColor: "warning",
      textColor: "warning.main",
    };
  }
  // Company shared folder (main shared folder or company-wide shared folders)
  if (isSharedFolder) {
    return {
      displayName: companyName + " Shared",
      iconColor: "primary",
    };
  }
  // Company-wide shared folders from other users in the same company
  if (isCompanyShared) {
    return {
      displayName: folder.name,
      iconColor: "primary",
    };
  }
  // Return with shareVisibility-based color or inherit
  return { displayName: folder.name, iconColor };
};

const DocumentScopeSelector: React.FC<DocumentScopeSelectorProps> = ({
  value,
  onChange,
  companyId,
  companyName,
  getToken,
  eventListener,
  sx,
  tooltip,
  disabled = false,
}) => {
  const [folders, setFolders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    fetchDocuments();
  }, [companyId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/documents/folders`, {
        params: {
          companyId: companyId,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const retFolders = (response.data.folders || []).map((folder: any) => {
        if (folder.name === "shared" && folder.path === "/") {
          return { ...folder, id: "/shared" };
        }
        return folder;
      });
      setFolders(retFolders);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFolderExpansion = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const getValuePath = (doc: any) => {
    if (doc.path === "/") {
      return doc.path + doc.name;
    }
    return doc.path + "/" + doc.name;
  };

  const findDocumentById = (
    id: string,
    folderList: any[] = folders
  ): any | null => {
    for (const doc of folderList) {
      if (doc.id === id) {
        return doc;
      }
      if (doc.folders && doc.folders.length > 0) {
        const found = findDocumentById(id, doc.folders);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const handleDocumentChange = (e: any) => {
    const selectedValue = e.target.value;
    console.log("Selected value:", e.target);

    if (selectedValue === "root") {
      onChange("root", "/");
    } else if (selectedValue === "tags") {
      onChange("tags", "/");
    } else {
      const selectedDoc = findDocumentById(selectedValue);
      console.log("Selected document:", selectedDoc);
      const currentPath =
        selectedDoc.path === "/"
          ? "/" + selectedDoc.name
          : selectedDoc.path + "/" + selectedDoc.name;
      onChange(selectedValue, currentPath);
    }
  };

  const renderFolders = (
    folderList: any[],
    level: number = 0
  ): React.ReactNode[] => {
    const items: React.ReactNode[] = [];

    folderList.forEach((folder) => {
      const isFile = folder.type !== "folder";
      const hasChildren =
        !isFile && folder.folders && folder.folders.length > 0;
      const isExpanded = expandedFolders.has(folder.id);
      const { displayName, iconColor, textColor } = getFolderDisplayProps(
        folder,
        companyName
      );

      items.push(
        <MenuItem
          key={folder.id}
          value={folder.id}
          sx={{
            pl: 2 + level * 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" flex={1}>
            {isFile ? (
              <FileIcon sx={{ mr: 1 }} />
            ) : iconColor === "warning" ? (
              <Source sx={{ mr: 1 }} color="warning" />
            ) : hasChildren ? (
              <Source sx={{ mr: 1 }} color={iconColor} />
            ) : (
              <Source sx={{ mr: 1 }} color={iconColor} />
            )}
            <Typography sx={textColor ? { color: textColor } : undefined}>
              {displayName}
            </Typography>
          </Box>
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => toggleFolderExpansion(folder.id, e)}
              sx={{ ml: 1 }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </MenuItem>
      );

      if (hasChildren && isExpanded) {
        items.push(...renderFolders(folder.folders, level + 1));
      }
    });

    return items;
  };

  const renderSelectedValue = (selected: any) => {
    // Show loading indicator while loading
    if (loading) {
      return (
        <Box display="flex" alignItems="center">
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography color="text.secondary">Loading documents...</Typography>
        </Box>
      );
    }

    // Handle special cases
    if (selected === "tags") {
      return (
        <Box display="flex" alignItems="center">
          <Sell sx={{ mr: 1 }} />
          Document Tags
        </Box>
      );
    }
    if (selected === "root") {
      return (
        <Box display="flex" alignItems="center">
          <Source sx={{ mr: 1 }} />/
        </Box>
      );
    }
    if (selected === "/shared") {
      return (
        <Box display="flex" alignItems="center">
          <Source sx={{ mr: 1 }} color="primary" />
          {companyName} Shared
        </Box>
      );
    }

    // Handle folder/file selection
    const selectedDoc = findDocumentById(selected);
    if (selectedDoc) {
      const isFile = selectedDoc.type !== "folder";
      const { displayName, iconColor, textColor } = getFolderDisplayProps(
        selectedDoc,
        companyName
      );

      return (
        <Box display="flex" alignItems="center">
          {isFile ? (
            <FileIcon sx={{ mr: 1 }} />
          ) : iconColor === "warning" ? (
            <Source sx={{ mr: 1 }} color="warning" />
          ) : (
            <Source sx={{ mr: 1 }} color={iconColor} />
          )}
          <Typography
            component="span"
            sx={textColor ? { color: textColor } : undefined}
          >
            {displayName}
          </Typography>
        </Box>
      );
    }

    return selected;
  };

  return (
    <FormControl fullWidth sx={sx}>
      <InputLabel>Document Scope</InputLabel>
      <Select
        value={value}
        label="Document Scope"
        onChange={handleDocumentChange}
        disabled={loading || disabled}
        renderValue={renderSelectedValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        <MenuItem key={"tags"} value={"tags"}>
          <IconMenuItem name={"Document Tags"} icon={<Sell />} />
        </MenuItem>
        <MenuItem key={"root"} value={"root"}>
          <IconMenuItem name={"/"} icon={<Source />} />
        </MenuItem>
        {loading ? (
          <MenuItem disabled>
            <Typography color="text.secondary">Loading documents...</Typography>
          </MenuItem>
        ) : (
          renderFolders(folders)
        )}
      </Select>
    </FormControl>
  );
};

export default DocumentScopeSelector;
